'use client'

import { useLocale, useTranslations } from 'next-intl'
import { BoxIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatNumber } from '@/lib/utils'

type PropsType = {
  title: string
  items: string[] | Record<string, any>[] | Record<string, string>
}

const ProductImportEffectedItems = ({ title, items }: PropsType) => {
  const t = useTranslations()
  const locale = useLocale()

  // Check if items is a plain object (Record<string, string>) - for failedItems
  const isPlainObject = !Array.isArray(items) && typeof items === 'object'

  // Check if items are object array (Record<string, any>[]) or string array
  const isObjectArray = Array.isArray(items) && items.length > 0 && typeof items[0] === 'object'

  // Get column headers from the first object in an array
  const columns = isObjectArray && items.length > 0 ? Object.keys(items[0] as Record<string, any>) : []

  // Get count based on item type
  const itemCount = isPlainObject ? Object.keys(items as Record<string, string>).length : (items as any[]).length

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BoxIcon className="h-5 w-5" />
          <span>{title}</span>
          <Badge variant="outline" className="font-mono text-xs">
            {formatNumber(itemCount, locale)}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {itemCount > 0 ? (
          isPlainObject ? (
            // Render as a table for a plain object (key-value pairs)
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">{t('code')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('error')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(items as Record<string, string>).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{key}</TableCell>
                      <TableCell className="whitespace-nowrap">{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : isObjectArray ? (
            // Render as a table for object arrays
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((column, index) => (
                      <TableHead key={index} className="whitespace-nowrap">
                        {column}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(items as Record<string, any>[]).map((item, index) => (
                    <TableRow key={index}>
                      {columns.map((column, colIndex) => (
                        <TableCell key={colIndex} className="whitespace-nowrap">
                          {item[column]?.toString() || '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            // Render as badges for string arrays
            <div className="flex flex-row flex-wrap gap-2">
              {(items as string[]).map((item, index) => (
                <Badge key={index} variant="outline" className="font-mono text-xs">
                  {item}
                </Badge>
              ))}
            </div>
          )
        ) : (
          <Badge variant="outline" className="font-mono text-xs">
            {t('none')}
          </Badge>
        )}
      </CardContent>
    </Card>
  )
}

export default ProductImportEffectedItems
